/**
 * This is a list of all remaining angularJS routes.
 *
 * The angular app is only loaded if the pathname matches one of these known routes. The list of known routes is
 * kept in `@hotjar/webapp-shell/src/routes/FallbackRouteHandler.tsx`. If routes are removed
 * from this config, please rememeber to sync the change across with the list of known paths
 * in the webapp-shell.
 */
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { LOGIN } from '@hotjar/webapp/constants/routes/auth';
import { redirectTo } from '@hotjar/webapp/utils/angular-router-helper';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import { TEAM } from '@hotjar/webapp/constants/routes';

angular.module('hjApp').config(function ($stateProvider) {
    $stateProvider
        .state('item-unavailable', {
            title: 'Item Unavailable',
            template: `
              <react-item-unavailable-route />
            `,
            pageImg: require('@hotjar/shared-assets/images/error-404.png'),
            pageTitle: 'Item unavailable',
            pageDescription: 'This item has either been deleted or is not available.',
            pageAction: 'dashboard-overview',
            requiredAccess: 'none'
        })
        .state('404', {
            title: 'Page Not Found',
            template: `<react-40x-page
                            title="'40x.404.title'"
                            description="'40x.404.description'"
                            img-src="'${require('@hotjar/shared-assets/images/error-404.png')}'"
                            redirect-to-login-page="redirectToLoginPage"
                        ></react-40x-page>`,
            requiredAccess: 'none',
            controller($scope) {
                $scope.redirectToLoginPage = () => browserHistory.replace(LOGIN.path);
            }
        })
        .state('403', {
            title: 'Request Forbidden',
            template: `<react-40x-page
                            title="'40x.403.title'"
                            description="'40x.403.description'"
                            img-src="'${require('@hotjar/shared-assets/images/error-403.png')}'"
                            redirect-to-login-page="redirectToLoginPage"
                        ></react-40x-page>`,
            requiredAccess: 'none',
            controller($scope) {
                $scope.redirectToLoginPage = () => browserHistory.replace(LOGIN.path);
            }
        })
        .state('react', {
            template: '',
            title: null,
            requiredAccess: 'none'
        });
});

angular.module('hjProfileModule').config(($stateProvider, $urlRouterProvider) => {
    $urlRouterProvider.when('/profile', redirectTo('profile.details'));

    $stateProvider.state('profile', {
        title: 'Profile',
        url: '/profile',
        template: '<ui-view/>'
    });
});

angular.module('hjApp.user').config(($urlRouterProvider) => {
    // legacy user support
    $urlRouterProvider.when('/user/invite/:organizationId', function ($match) {
        navigateTo(TEAM.INVITE.path, {
            organizationId: $match.organizationId
        });
    });
    $urlRouterProvider.when('/user/list', function () {
        navigateTo(TEAM.LIST.path);
    });
    $urlRouterProvider.when('/user', function () {
        navigateTo(TEAM.LIST.path);
    });
    $urlRouterProvider.when('/team', function () {
        navigateTo(TEAM.LIST.path);
    });
});

angular.module('hjSettingsModule').config(function ($stateProvider) {
    $stateProvider
        .state('settings', {
            title: 'Settings',
            url: '/settings',
            template: '<ui-view/>'
        })
        .state('settings.invoices', {
            title: 'Invoices',
            url: '/invoices',
            template: '<ui-view>',
            controller: function ($state, Account) {
                if (Account.getOwned()) {
                    $state.go('settings.invoices.owner', null, { location: false });
                } else {
                    $state.go('settings.invoices.no_access', null, { location: false });
                }
            }
        })
        .state('settings.invoices.owner', {
            title: 'Invoices',
            template: require('../../settings/html/invoices.html'),
            controller: 'InvoicesCtrl',
            requiresAccountAdminAccess: true
        })

        /**
         * Two billing pages are defined which cater for different pricing structures.
         * Main billing state simply redirects based on account features.
         */
        .state('settings.billing', {
            title: 'Plans & Billing',
            url: '/billing',
            template: '<ui-view/>',
            controller: function ($state, Account) {
                const ownedAccount = Account.getOwned();
                if (ownedAccount && isBillingV3Enabled(ownedAccount)) {
                    navigateTo('/settings/subscriptions');
                } else if (ownedAccount) {
                    $state.go('settings.billing.v2', null, { location: false });
                } else {
                    $state.go('settings.billing.no_access', null, { location: false });
                }
            }
        })
        .state('settings.billing.v2', {
            title: 'Plans & Billing',
            template: require('../../settings/html/billingV2.html'),
            controller: 'BillingV2Ctrl',
            requiresAccountAdminAccess: true
        })
        .state('settings.billing.no_access', {
            title: 'Plans & Billing',
            template: '<react-billing-access-denied></react-billing-access-denied>'
        })
        .state('settings.invoices.no_access', {
            title: 'Invoices',
            template: '<react-invoices-access-denied></react-invoices-access-denied>'
        });
});

const FEEDBACK_DEFAULT_STATE = {
    title: 'Feedback',
    url: '/responses',
    template: require('../../feedback/html/feedback-responses.html'),
    controller: 'FeedbackResponsesCtrl',
    params: {
        newToolCreated: false
    }
};

const FEEDBACK_RESPONSES_STATE = {
    title: 'Feedback',
    url: '/responses/:feedbackWidgetId?frid',
    template: require('../../feedback/html/feedback-responses.html'),
    controller: 'FeedbackResponsesCtrl',
    params: {
        newToolCreated: false
    },
    reloadOnSearch: false
};

const FEEDBACK_CREATE_STATE = {
    title: 'Feedback',
    url: '/create/:copyId',
    template: require('../../feedback/html/feedback-edit-create.html'),
    controller: 'FeedbackEditCreateCtrl',
    requiredAccess: 'write'
};

const FEEDBACK_EDIT_STATE = {
    title: 'Feedback',
    url: '/edit/:feedbackWidgetId/:activeFlowSection',
    params: {
        activeFlowSection: { value: null, squash: true }
    },
    template: require('../../feedback/html/feedback-edit-create.html'),
    controller: 'FeedbackEditCreateCtrl',
    requiredAccess: 'write'
};

angular.module('hjFeedbackModule').config(function ($stateProvider, $urlRouterProvider) {
    const redirectToFeedbackResponses = (matchURL, redirectState) => {
        $urlRouterProvider.when(matchURL, [
            '$state',
            '$match',
            'Restangular',
            'restDefaultHandlers',
            function ($state, $match, Restangular, restDefaultHandlers) {
                var rSite = Restangular.one('sites', $match.siteId);
                rSite
                    .all('feedback')
                    .getList({})
                    .then(
                        function (feedbackWidgets) {
                            switch (feedbackWidgets.length) {
                                case 1:
                                    $state.go(redirectState, {
                                        feedbackWidgetId: feedbackWidgets[0].id
                                    });
                                    break;
                                default:
                                    $state.go(redirectState);
                                    break;
                            }
                        },
                        function (res) {
                            restDefaultHandlers.onError(res);
                        }
                    );
            }
        ]);
    };
    redirectToFeedbackResponses('/sites/:siteId/feedback', 'feedback.responses');

    $stateProvider
        .state('feedback', {
            title: 'Feedback',
            url: '/sites/:siteId/feedback',
            template: '<ui-view/>',
            params: {
                newToolCreated: false
            }
        })
        .state('feedback.default', FEEDBACK_DEFAULT_STATE)
        .state('feedback.responses', FEEDBACK_RESPONSES_STATE)
        .state('feedback.create', FEEDBACK_CREATE_STATE)
        .state('feedback.edit', FEEDBACK_EDIT_STATE);
});

const CREATE_SURVEY_STATE = {
    title: 'New Survey',
    url: '/create/:copyId?templateId&category&activeFlowSection',
    template: require('../../polls/html/polls-edit-create.html'),
    controller: 'PollsEditCreateCtrl',
    requiredAccess: 'write',
    params: {
        dataFromTemplate: null,
        templateId: null,
        activeFlowSection: null,
        copyId: { squash: true, value: null }
    }
};

const EDIT_SURVEY_STATE = {
    title: 'Edit Survey',
    url: '/edit/:pollId',
    params: {
        activeFlowSection: { value: null, squash: true }
    },
    template: require('../../polls/html/polls-edit-create.html'),
    controller: 'PollsEditCreateCtrl',
    requiredAccess: 'write'
};

angular.module('hjSurveysModule').config(function ($stateProvider, $urlRouterProvider) {
    // basic support for legacy surveys and polls URLs
    $urlRouterProvider.when('/sites/:siteId/polls', redirectTo('surveys'));
    $urlRouterProvider.when('/sites/:siteId/polls/create/:copyId', redirectTo('surveys.create'));
    $urlRouterProvider.when('/sites/:siteId/polls/edit/:pollId', redirectTo('surveys.edit'));
    $urlRouterProvider.when('/sites/:siteId/polls/results/:pollId', redirectTo('surveys.results'));
    $urlRouterProvider.when('/sites/:siteId/polls/performance/:pollId', redirectTo('surveys.performance'));

    $stateProvider
        .state('surveys', {
            title: 'Surveys',
            url: '/sites/:siteId/surveys',
            template: '<ui-view/>'
        })
        .state('surveys.create', CREATE_SURVEY_STATE)
        .state('surveys.edit', EDIT_SURVEY_STATE);
});

angular.module('hjSiteModule').config(function ($stateProvider, $urlRouterProvider) {
    $urlRouterProvider.when('/site', redirectTo('site.list'));
    $stateProvider.state('site', {
        title: 'Sites & Organizations',
        url: '/site',
        template: '<ui-view/>'
    });
});
